import React from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface PageFlipProps {
  children: React.ReactNode;
  pageKey: string | number;
  direction?: 1 | -1;
  className?: string;
  onFlipStart?: () => void;
  onFlipComplete?: () => void;
}

// 3D page flip animation variants
const pageFlipVariants: Variants = {
  enter: (direction: number) => ({
    rotateY: direction > 0 ? 90 : -90,
    opacity: 0,
    scale: 0.95,
    x: direction > 0 ? 100 : -100,
    transformOrigin: direction > 0 ? 'left center' : 'right center',
  }),
  center: {
    rotateY: 0,
    opacity: 1,
    scale: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: (direction: number) => ({
    rotateY: direction > 0 ? -90 : 90,
    opacity: 0,
    scale: 0.95,
    x: direction > 0 ? -100 : 100,
    transformOrigin: direction > 0 ? 'right center' : 'left center',
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

// Simpler slide flip for less dramatic transitions
const slideFlipVariants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
    scale: 0.9,
    rotate: direction > 0 ? 3 : -3,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
    scale: 0.9,
    rotate: direction > 0 ? -3 : 3,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

export const PageFlip: React.FC<PageFlipProps> = ({
  children,
  pageKey,
  direction = 1,
  className,
  onFlipStart,
  onFlipComplete,
}) => {
  return (
    <AnimatePresence mode="wait" custom={direction}>
      <motion.div
        key={pageKey}
        custom={direction}
        variants={pageFlipVariants}
        initial="enter"
        animate="center"
        exit="exit"
        onAnimationStart={onFlipStart}
        onAnimationComplete={onFlipComplete}
        className={cn('relative', className)}
        style={{
          perspective: 1200,
          transformStyle: 'preserve-3d',
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Simpler slide transition (less 3D)
export const PageSlide: React.FC<PageFlipProps> = ({
  children,
  pageKey,
  direction = 1,
  className,
  onFlipStart,
  onFlipComplete,
}) => {
  return (
    <AnimatePresence mode="wait" custom={direction}>
      <motion.div
        key={pageKey}
        custom={direction}
        variants={slideFlipVariants}
        initial="enter"
        animate="center"
        exit="exit"
        onAnimationStart={onFlipStart}
        onAnimationComplete={onFlipComplete}
        className={cn('relative', className)}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Notebook page curl effect (more realistic)
export const NotebookPageFlip: React.FC<PageFlipProps & { showPageEdge?: boolean }> = ({
  children,
  pageKey,
  direction = 1,
  className,
  showPageEdge = true,
  onFlipStart,
  onFlipComplete,
}) => {
  const curlVariants: Variants = {
    enter: (dir: number) => ({
      clipPath: dir > 0
        ? 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)'
        : 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)',
      opacity: 0.5,
    }),
    center: {
      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
      opacity: 1,
      transition: {
        clipPath: { duration: 0.8, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.3 },
      },
    },
    exit: (dir: number) => ({
      clipPath: dir > 0
        ? 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)'
        : 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)',
      opacity: 0.5,
      transition: {
        clipPath: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.3, delay: 0.3 },
      },
    }),
  };

  return (
    <AnimatePresence mode="wait" custom={direction}>
      <motion.div
        key={pageKey}
        custom={direction}
        variants={curlVariants}
        initial="enter"
        animate="center"
        exit="exit"
        onAnimationStart={onFlipStart}
        onAnimationComplete={onFlipComplete}
        className={cn('relative', className)}
      >
        {/* Page shadow during flip */}
        {showPageEdge && (
          <motion.div
            className="absolute inset-y-0 w-8 pointer-events-none z-10"
            style={{
              left: direction > 0 ? 0 : 'auto',
              right: direction < 0 ? 0 : 'auto',
              background: direction > 0
                ? 'linear-gradient(90deg, rgba(0,0,0,0.1), transparent)'
                : 'linear-gradient(-90deg, rgba(0,0,0,0.1), transparent)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default PageFlip;
